import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { 
  Clock, 
  Copy, 
  Lightbulb, 
  ShieldCheck, 
  Users, 
  TrendingDown, 
  Rocket,
  ArrowRight
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const icons = [Clock, Copy, Lightbulb, ShieldCheck, Users, TrendingDown];

export const ROISection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { t } = useLanguage();
  const data = t('roi');

  return (
    <section
      id="roi"
      data-testid="roi-section"
      className="relative py-16 sm:py-24 overflow-hidden bg-zinc-900"
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <h2
            data-testid="roi-headline"
            className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-4 text-zinc-100"
          >
            {data.headline}{' '}
            <span className="font-semibold text-emerald-600">
              {data.headlineHighlight}
            </span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-base leading-relaxed">
            {data.subheadline}
          </p>
        </motion.div>

        {/* Outcomes Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
          {data.outcomes.map((outcome, index) => {
            const Icon = icons[index];
            return (
              <motion.div
                key={outcome.title}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                data-testid={`roi-outcome-${index}`}
                className="bg-zinc-950 border border-zinc-800 rounded-xl p-5 hover:shadow-md hover:border-zinc-300 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-zinc-800 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-zinc-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-zinc-100">
                      {outcome.stat}
                    </div>
                    <h3 className="text-sm font-medium text-zinc-300 mt-0.5">{outcome.title}</h3>
                    <p className="text-xs text-zinc-400 mt-1">{outcome.description}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          {/* Section title */}
          <div className="text-center mb-10">
            <h3 className="text-xl sm:text-2xl font-light tracking-tight text-zinc-100">
              {data.timelineTitle}{' '}
              <span className="font-semibold gradient-text">
                {data.timelineHighlight}
              </span>
            </h3>
          </div>

          {/* Timeline cards */}
          <div className="relative">
            {/* Connection line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-zinc-800 -translate-y-1/2" />
            
            <div className="grid lg:grid-cols-3 gap-5">
              {data.timeline.map((item, index) => (
                <motion.div
                  key={item.month}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.7 + index * 0.15 }}
                  className="relative"
                >
                  {/* Connector dot */}
                  <div className="hidden lg:block absolute -top-2.5 left-1/2 -translate-x-1/2">
                    <div className="w-5 h-5 rounded-full bg-zinc-950 border-2 border-zinc-800 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-blue-500" />
                    </div>
                  </div>

                  <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-5 hover:shadow-md hover:border-zinc-300 transition-all h-full">
                    <div className="text-xs font-semibold tracking-wider text-blue-500 uppercase mb-2">
                      {item.month}
                    </div>
                    <h4 className="text-base font-semibold text-zinc-100 mb-2">{item.title}</h4>
                    <p className="text-sm text-zinc-400">{item.description}</p>
                  </div>

                  {/* Arrow to next */}
                  {index < 2 && (
                    <div className="hidden lg:flex absolute top-1/2 -right-2.5 -translate-y-1/2 z-10">
                      <ArrowRight className="w-5 h-5 text-zinc-300" />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ROISection;
